import { Modal, Form, Input, Select, Upload, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Props {
    open: boolean;
    setOpen: (v: boolean) => void;
    onCreate: (data: any) => void;
    editingCategory?: any;
}

export default function CategoryModal({
    open,
    setOpen,
    onCreate,
    editingCategory,
}: Props) {
    const [form] = Form.useForm();
    const [image, setImage] = useState<string>("");
    const { t } = useTranslation();

    const getBase64 = (file: any) =>
        new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
        });

    const uploadProps = {
        beforeUpload: async (file: any) => {
            const base64: any = await getBase64(file);
            setImage(base64);
            return false;
        },
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            onCreate({
                ...values,
                image,
            });

            form.resetFields();
            setImage("");
            setOpen(false);
        } catch (error) {
            console.log("Form error:", error);
        }
    };

    useEffect(() => {
        if (editingCategory) {
            form.setFieldsValue(editingCategory);
            setImage(editingCategory.image || "");
        } else {
            form.resetFields();
            setImage("");
        }
    }, [editingCategory, form]);

    return (
        <Modal
            title={editingCategory ? t('products_cat.modal_edit') : t('products_cat.modal_add')}
            open={open}
            onCancel={() => {
                setOpen(false);
                form.resetFields();
                setImage("");
            }}
            footer={null}
            width={520}
            className="
                dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                dark:[&_.ant-modal-content]:bg-gray-800
                dark:[&_.ant-modal-title]:text-white
                dark:[&_.ant-modal-close]:text-white
                dark:[&_.ant-modal-header]:!bg-transparent
            "
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t('products_cat.label_name')}
                    name="name"
                    rules={[{ required: true, message: t('products_cat.validate_name') }]}
                >
                    <Input className="dark:bg-[#141414] dark:border-[#303030] t-text-white" />
                </Form.Item>

                <Form.Item label={t('products_cat.label_description')} name="description">
                    <Input className="dark:bg-[#141414] dark:border-[#303030] t-text-white" />
                </Form.Item>

                <Form.Item label={t('products_cat.label_status')} name="status" initialValue="1">
                    <Select
                        options={[
                            { label: t('products_cat.status_active'), value: "1" },
                            { label: t('products_cat.status_inactive'), value: "0" },
                        ]}
                    />
                </Form.Item>

                <Form.Item label={t('products_cat.label_image')} name="image">
                    <Upload {...uploadProps} listType="picture-card" maxCount={1}>
                        {image ? (
                            <img
                                src={image}
                                alt="category"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div>
                                <PlusOutlined />
                                <div>{t('products_cat.upload')}</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>

                <div className="flex justify-end gap-2">
                    <Button
                        onClick={() => {
                            setOpen(false);
                            form.resetFields();
                            setImage("");
                        }}
                    >
                        {t('products_cat.cancel')}
                    </Button>

                    <Button type="primary" onClick={handleSubmit}>
                        {editingCategory ? t('products_cat.update') : t('products_cat.create')}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}